import { NewPasswordFields } from "@/components/account/NewPasswordFields";
import { STANDALONE_LINK_CLASS } from "@/components/layout/standalone-link";
import { AvatarBox } from "@/components/logo";
import { PII_MASK_CLASS } from "@/lib/analytics";
import { handleActionError } from "@/lib/errors";
import { validateNewPassword } from "@/lib/password";
import { Alert, Button, Heading, Hint, Input, Text } from "@medusajs/ui";
import sdk from "@repo/api-sdk";
import {
  createRoute,
  Link,
  useNavigate,
  useSearch
} from "@tanstack/react-router";
import { useCallback, useState } from "react";
import * as z from "zod";
import { rootRoute } from "../$rootRoute";

export const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "reset-password",
  validateSearch: (
    search?: Record<string, unknown>
  ): { token?: string; email?: string } => ({
    token: typeof search?.token === "string" ? search.token : undefined,
    email: typeof search?.email === "string" ? search.email : undefined
  }),
  component: ResetPasswordPage
});

/**
 * Password reset - both halves of it.
 *
 * With no `?token=`, this asks for an email and requests a reset link. With a
 * token (the link the backend emails, `<storefront>/reset-password?token=…`), it
 * takes the new password and submits it against that token.
 *
 * This exists because concierge links are how salons *first* get in, and a
 * salon who sets a password then forgets it otherwise has no way back that does
 * not involve messaging an operator (KAN-42).
 *
 * The request step always reports success, whether or not the address is known:
 * telling a stranger which emails have accounts is an account-enumeration
 * oracle, and the salon who owns the inbox learns the truth from the inbox.
 */
function ResetPasswordPage() {
  const { token, email: emailFromLink } = useSearch({
    from: resetPasswordRoute.id
  });

  return token ? (
    <ResetPasswordShell>
      <SetNewPasswordForm token={token} email={emailFromLink} />
    </ResetPasswordShell>
  ) : (
    <ResetPasswordShell>
      <RequestResetForm />
    </ResetPasswordShell>
  );
}

function ResetPasswordShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-ui-bg-subtle flex min-h-dvh w-dvw items-center justify-center">
      <div className="m-4 flex w-full max-w-[300px] flex-col items-center">
        <AvatarBox checked={false} />
        {children}
        <span className="text-ui-fg-muted txt-small my-6">
          <Link to="/login" className={STANDALONE_LINK_CLASS}>
            Back to sign in
          </Link>
        </span>
      </div>
    </div>
  );
}

const emailSchema = z.email();
const EMAIL_PROBLEM_ID = "reset-email-problem";
const PASSWORD_PROBLEM_ID = "reset-password-problem";

function RequestResetForm() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [isPending, setIsPending] = useState(false);
  // Set only by a failed submit. Neither the native `required` nor
  // `type="email"`: the Input paints its error ring on `:invalid`, i.e. on an
  // empty field at first paint and on a half-typed address mid-keystroke, and
  // the browser's own popup would pre-empt this message. Like the login form,
  // the email is a plain text input validated on submit.
  const [problem, setProblem] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      const identifier = email.trim();
      if (identifier === "") {
        setProblem("Enter your email.");
        return;
      }
      if (!emailSchema.safeParse(identifier).success) {
        setProblem("Enter a valid email address.");
        return;
      }
      setProblem(null);
      setIsPending(true);
      try {
        await sdk.auth.resetPassword("customer", "emailpass", {
          identifier
        });
      } catch {
        // Swallowed on purpose: a failure here would otherwise reveal whether
        // the address has an account. The confirmation below is unconditional.
      } finally {
        setIsPending(false);
        setSent(true);
      }
    },
    [email]
  );

  if (sent) {
    return (
      <div className="flex flex-col items-center gap-y-1 text-center">
        <Heading>Check your email</Heading>
        {/* Echoes back whatever was typed into the email field, which the input
            masking would otherwise have covered (ADR-0006). */}
        <Text size="small" className={`text-ui-fg-subtle ${PII_MASK_CLASS}`}>
          If an account exists for {email}, we&apos;ve sent a link to reset your
          password.
        </Text>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-col items-center text-center">
        <Heading>Reset your password</Heading>
        <Text size="small" className="text-ui-fg-subtle">
          We&apos;ll email you a link to set a new one.
        </Text>
      </div>
      <form
        noValidate
        onSubmit={handleSubmit}
        className="flex w-full flex-col gap-y-4"
      >
        <Input
          inputMode="email"
          aria-required
          aria-invalid={problem !== null}
          aria-describedby={problem ? EMAIL_PROBLEM_ID : undefined}
          autoComplete="email"
          placeholder="Email"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setProblem(null);
          }}
          className="bg-ui-bg-field-component"
          aria-label="Email"
        />
        {problem && (
          <div className="text-center">
            <Hint
              id={EMAIL_PROBLEM_ID}
              role="alert"
              className="inline-flex"
              variant="error"
            >
              {problem}
            </Hint>
          </div>
        )}
        <Button className="w-full" type="submit" isLoading={isPending}>
          Send reset link
        </Button>
      </form>
    </>
  );
}

function SetNewPasswordForm({
  token,
  email
}: {
  token: string;
  email?: string;
}) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Set only by a failed validation, so the fields never look wrong before the
  // user has tried to submit.
  const [invalid, setInvalid] = useState(false);
  const [isPending, setIsPending] = useState(false);

  // Editing a field the failed validation flagged takes the flag - and the
  // message that goes with it - away, as the login form does. A server error
  // (`invalid` false) stays until the next submit.
  const clearValidation = useCallback(() => {
    if (!invalid) return;
    setInvalid(false);
    setError(null);
  }, [invalid]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setError(null);
      setInvalid(false);

      const problem = validateNewPassword(password, confirmation);
      if (problem) {
        setError(problem);
        setInvalid(true);
        return;
      }

      setIsPending(true);
      try {
        await sdk.auth.updateProvider(
          "customer",
          "emailpass",
          // `email` rides along when the link carries it; the token identifies
          // the account either way.
          email ? { email, password } : { password },
          token
        );
        navigate({ to: "/login", replace: true });
      } catch (caught) {
        setError(
          handleActionError(caught, {
            boundary: "reset password",
            fallback: "This reset link is no longer valid. Request a new one.",
            trustClientErrors: true
          })
        );
      } finally {
        setIsPending(false);
      }
    },
    [password, confirmation, email, token, navigate]
  );

  return (
    <>
      <div className="mb-4 flex flex-col items-center text-center">
        <Heading>Choose a new password</Heading>
        <Text size="small" className="text-ui-fg-subtle">
          You&apos;ll use it to sign in from now on.
        </Text>
      </div>
      <form onSubmit={handleSubmit} className="flex w-full flex-col gap-y-4">
        <NewPasswordFields
          password={password}
          confirmation={confirmation}
          onPasswordChange={(value) => {
            clearValidation();
            setPassword(value);
          }}
          onConfirmationChange={(value) => {
            clearValidation();
            setConfirmation(value);
          }}
          passwordLabel="New password"
          confirmationLabel="Confirm new password"
          invalid={invalid}
          describedBy={error ? PASSWORD_PROBLEM_ID : undefined}
        />
        {error && (
          <Alert
            id={PASSWORD_PROBLEM_ID}
            role="alert"
            className="bg-ui-bg-base items-center p-2"
            variant="error"
          >
            {error}
          </Alert>
        )}
        <Button className="w-full" type="submit" isLoading={isPending}>
          Save password
        </Button>
      </form>
    </>
  );
}
